'use client';

import { ReactElement } from 'react';
import { createRoot, Root } from 'react-dom/client';

import { useLoading } from '@/hooks/use-loading.hook';
import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';

const PRINT_LOAD_TIMEOUT_MS = 1500;

interface GenerateGuideOptions {
  guideCode: string;
  component: ReactElement;
}

export function useGenerateGuide() {
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { toast } = useToast();
  const logger = getLogger('appointments.generate-guide');

  const generateGuide = async ({ guideCode, component }: GenerateGuideOptions): Promise<void> => {
    const printContainerId = 'print-container';

    startLoading();

    let root: Root | null = null;
    let container: HTMLDivElement | null = null;

    const cleanup = () => {
      if (root) {
        root.unmount();
        root = null;
      }
      if (container) {
        container.remove();
        container = null;
      }
      stopLoading();
    };

    try {
      container = document.createElement('div');
      container.id = printContainerId;
      document.body.appendChild(container);

      root = createRoot(container);
      root.render(component);

      await new Promise((resolve) => setTimeout(resolve, 100));

      const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map((el) => el.outerHTML)
        .join('\n');

      const printHtml = `
        <!DOCTYPE html>
        <html lang="pt-BR">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Guia ${guideCode}</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&display=swap" rel="stylesheet">
            ${styles}
            <style>
              * {
                font-family: 'Nunito', sans-serif;
              }
              body {
                margin: 0;
                padding: 40px;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                background-color: #f3f4f6;
                min-height: 100vh;
              }
              #print-content {
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
              }
              @media screen and (max-width: 850px) {
                body {
                  padding: 16px;
                  background-color: #f3f4f6;
                }
                #print-content {
                  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                }
              }
              @media print {
                @page {
                  size: A4 portrait;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 0;
                  background-color: white;
                }
                #print-content {
                  box-shadow: none;
                  width: 794px !important;
                  min-height: 1123px !important;
                }
              }
            </style>
          </head>
          <body>
            <div id="print-content">
              ${container.innerHTML}
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(printHtml);
        printWindow.document.close();
        printWindow.focus();

        printWindow.addEventListener('load', async () => {
          try {
            const nunitoFontPromise = printWindow.document.fonts.load('400 16px Nunito');

            const logoImg = printWindow.document.getElementById('medprev-logo') as HTMLImageElement;
            const logoPromise = logoImg && !logoImg.complete
              ? new Promise((resolve) => {
                  logoImg.onload = () => resolve(undefined);
                  logoImg.onerror = () => resolve(undefined);
                })
              : Promise.resolve();

            const contentPromise = Promise.all([nunitoFontPromise, logoPromise]);
            const timeoutPromise = new Promise((resolve) => setTimeout(resolve, PRINT_LOAD_TIMEOUT_MS));

            await Promise.race([contentPromise, timeoutPromise]);

            printWindow.print();
          } catch {
            printWindow.print();
          } finally {
            cleanup();
          }
        });
      } else {
        logger.info('Print window blocked by the browser', {
          appointment_guide: { event: 'print_window_blocked' },
        });
        toast.warn('Não foi possível abrir a janela de impressão. Verifique se popups estão bloqueados.');
        cleanup();
      }
    } catch (error) {
      logger.error('Failed to generate the appointment guide', {
        appointment_guide: { event: 'generation_failed' },
        error,
      });
      toast.error('Erro ao gerar guia. Tente novamente.');
      cleanup();
    }
  };

  return {
    generateGuide,
    isLoading,
  };
}
